import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useManagerDrilldown } from "@/hooks/useSupervisorSummary";

/**
 * S2 Manager Drilldown (C4 P0-scope) — 4 sections for one team member
 * {overdue_tasks · meetings_no_report · clients_no_action · ar_risk_clients}.
 * NO full-analytics/sales-plan/presence. Out-of-team → backend 403 (AC-SH-MGR-004).
 */
export function ManagerDrilldownPanel({
  managerId,
  managerName,
  onClose,
}: {
  managerId: string;
  managerName: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isPending, isError, error } = useManagerDrilldown(managerId);
  const forbidden = isError && (error as { status?: number } | null)?.status === 403;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-foreground/30" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-background p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Деталі менеджера</div>
            <div className="text-lg font-semibold">{managerName ?? "—"}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Закрити">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {forbidden ? (
          <div className="rounded-xl border bg-card py-10 text-center text-sm text-muted-foreground">
            Цей менеджер поза вашою командою.
          </div>
        ) : isPending ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : isError || !data ? (
          <div className="rounded-xl border bg-card py-10 text-center text-sm text-muted-foreground">
            Не вдалося завантажити деталі.
          </div>
        ) : (
          <div className="space-y-4">
            <Section title="Прострочені задачі" count={data.sections.overdueTasks.length}>
              {data.sections.overdueTasks.map((t) => (
                <Row key={t.id} primary={t.title} secondary={t.accountName} onClick={() => navigate(`/tasks?open=${t.id}`)} />
              ))}
            </Section>
            <Section title="Зустрічі без звіту" count={data.sections.meetingsNoReport.length}>
              {data.sections.meetingsNoReport.map((m) => (
                <Row key={m.id} primary={m.title} secondary={m.accountName} onClick={() => navigate(`/meetings?open=${m.id}`)} />
              ))}
            </Section>
            <Section title="Клієнти без next-action" count={data.sections.clientsNoAction.length}>
              {data.sections.clientsNoAction.map((c) => (
                <Row key={c.id} primary={c.name} />
              ))}
            </Section>
            {data.sections.arRiskClients === null ? (
              <Section title="Боржники / AR-risk" subtitle="Дані BI/AR недоступні" />
            ) : (
              <Section title="Боржники / AR-risk" count={data.sections.arRiskClients.length}>
                {data.sections.arRiskClients.map((c) => (
                  <Row key={c.id} primary={c.name} secondary={`прострочка ${Math.round(c.overdue)}`} onClick={() => navigate(`/clients/${c.id}`)} />
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  subtitle,
  children,
}: {
  title: string;
  count?: number;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  const empty = count === 0;
  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <span className="text-sm font-semibold">{title}</span>
        {count !== undefined && (
          <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
        )}
      </div>
      {subtitle ? (
        <div className="px-4 py-3 text-xs text-muted-foreground">{subtitle}</div>
      ) : empty ? (
        <div className="px-4 py-3 text-xs text-muted-foreground">Немає</div>
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
}

function Row({
  primary,
  secondary,
  onClick,
}: {
  primary: string;
  secondary?: string | null;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="truncate text-sm">{primary || "—"}</div>
      {secondary && <div className="truncate text-xs text-muted-foreground">{secondary}</div>}
    </>
  );
  if (!onClick) {
    return <div className="border-b px-4 py-2.5 last:border-b-0">{inner}</div>;
  }
  return (
    <button type="button" onClick={onClick} className="block w-full border-b px-4 py-2.5 text-left last:border-b-0 hover:bg-muted">
      {inner}
    </button>
  );
}
